import * as React from "react";
import { union } from "lodash";
import { connect } from "react-redux";
import Dropzone from "react-dropzone";

import { DocTypes } from "@eave/shared/types/api";
import APIClient from "./APIClient";
import UploadConfirmation from "./UploadConfirmation";

require("../assets/stylesheets/components/shared/uploads.scss");

const FILE_SIZE_LIMIT_BYTES = 100 * 1024 * 1024;

enum UploadErrorReason {
  FileSizeTooBig = "size",
  InternalServerError = "server",
  PasswordProtected = "password",
}

interface IUploadError {
  filename: string;
  reason: UploadErrorReason;
}

const UploadError = ({ filename, reason }) => {
  const MAX_FILENAME_LENGTH = 30;
  let name = filename;
  if (name.length > MAX_FILENAME_LENGTH) {
    name = `${name.substr(0, MAX_FILENAME_LENGTH)}…`;
  }

  let msg = `Could not upload "${name}" because`;

  switch (reason) {
    case UploadErrorReason.FileSizeTooBig:
      msg = `${msg} it is 100 MB or larger. If you're uploading scanned documents, try saving them at a lower resolution. If you're uploading a PDF, you can break it into multiple files and try again.`;
      break;
    case UploadErrorReason.InternalServerError:
      msg = `${msg} an internal server error occurred. Please reload and try again, or contact engineering if this problem persists.`;
      break;
    case UploadErrorReason.PasswordProtected:
      msg = `${msg} it appears to be password-protected. Try again after removing the password. You can do this by opening the file and re-printing it to a new PDF without a password.`;
      break;
    default:
      msg = `${msg} we don't support this type of file.`;
  }

  return <div className="upload-error">{msg}</div>;
};

interface DocumentUploaderProps {
  documents: any[];
  documentType: DocTypes;
  prompt: string;
  onDelete?: Function;
  onUpload: Function;
  onError?: Function;
  onFocus?: Function;
  updateApplication: Function;
  borrowerEntity?: number;
  ownerEntityType?: string;
  ownerEntityID?: number;
  applicationID?: number;
  conditionID?: number;
  showOnlyCurrentUploadsOfType?: boolean;
  restrictToOneUpload?: boolean;
  className?: string;
}

interface State {
  currentDocIds: number[];
  uploadErrors: IUploadError[];
}

class DocumentUploader extends React.Component<DocumentUploaderProps, any> {
  _isMounted: Boolean;

  constructor(props: DocumentUploaderProps) {
    super(props);
    this.state = DocumentUploader.initialState();
  }

  static initialState(): State {
    return {
      currentDocIds: [],
      uploadErrors: [],
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentWillReceiveProps(nextProps: DocumentUploaderProps) {
    if (nextProps.documentType !== this.props.documentType) {
      this.setState(DocumentUploader.initialState());
    }
  }

  filteredDocuments() {
    const {
      documents,
      documentType,
      showOnlyCurrentUploadsOfType,
      ownerEntityType,
      ownerEntityID,
    } = this.props;

    if (!documents || !documents.length) {
      return [];
    }

    let docs = documents.filter(doc => doc.type === documentType);

    if (showOnlyCurrentUploadsOfType) {
      docs = docs.filter(doc => this.state.currentDocIds.includes(doc.id));
    }

    if (ownerEntityType && ownerEntityID) {
      docs = docs.filter(
        doc => doc.owner_entity_type === ownerEntityType && doc.owner_entity_id === ownerEntityID,
      );
    }

    return docs;
  }

  onDrop = files => {
    files.forEach(file => {
      this.uploadDocument(file);
    });
  };

  uploadDocument(file) {
    const {
      ownerEntityType,
      ownerEntityID,
      borrowerEntity,
      applicationID,
      conditionID,
      documentType,
      updateApplication,
      onUpload,
      onError,
      showOnlyCurrentUploadsOfType,
    } = this.props;

    if (file.size >= FILE_SIZE_LIMIT_BYTES) {
      this.setState((prevState: State) => ({
        ...prevState,
        uploadErrors: [
          {
            filename: file.name,
            reason: UploadErrorReason.FileSizeTooBig,
          },
        ],
      }));
      return;
    }

    let moreData: any = {};
    if (ownerEntityType && ownerEntityID) {
      moreData = {
        owner_entity_type: ownerEntityType,
        owner_entity_id: ownerEntityID,
      };
    }
    if (borrowerEntity) {
      moreData.borrower_entity = borrowerEntity;
    }
    if (applicationID) {
      moreData.application_id = applicationID;
    }
    if (conditionID) {
      moreData.condition_id = conditionID;
    }

    APIClient.dropzoneUpload("/loan_application/attachments", documentType, file, moreData)
      .then(({ application, documents, rejected }) => {
        updateApplication("UPDATE_APPLICATION", application);

        if (!this._isMounted) {
          return;
        }

        const doc = documents[0];
        const rej = rejected[0];

        this.setState(
          (prevState: State) => ({
            currentDocIds: doc ? union(prevState.currentDocIds, [doc.id]) : prevState.currentDocIds,
            uploadErrors: rej ? [rej] : [], // right now only show most recent
          }),
          () => {
            if (doc && onUpload && showOnlyCurrentUploadsOfType) {
              onUpload(this.state.currentDocIds);
            } else if (doc && onUpload) {
              onUpload(doc.id);
            } else if (rej && onError) {
              onError(rej);
            }
          },
        );
      })
      .catch(() => {
        this.setState((prevState: State) => ({
          ...prevState,
          uploadErrors: [
            {
              filename: file.name,
              reason: UploadErrorReason.InternalServerError,
            },
          ],
        }));
      });
  }

  deleteDocument = (docId: number) => {
    const { onDelete } = this.props;

    APIClient.deleteRequest(`/loan_application/attachments/${docId}`).then((resp: any) => {
      this.props.updateApplication("UPDATE_APPLICATION", resp.application);
      if (this._isMounted) {
        this.setState({
          currentDocIds: this.state.currentDocIds.filter(id => id !== docId),
        });
      }
      if (onDelete) {
        onDelete(docId);
      }
    });
  };

  render() {
    let className = "uploads ";
    if (this.props.className) {
      className += this.props.className;
    }

    return (
      <div className={className}>
        <Dropzone
          onDrop={this.onDrop}
          className="dropzone"
          activeClassName="active"
          multiple={!this.props.restrictToOneUpload}
        >
          {this.props.prompt}
        </Dropzone>
        <div className="upload-errors">
          {this.state.uploadErrors.map(err => (
            <UploadError key={err.filename} filename={err.filename} reason={err.reason} />
          ))}
        </div>
        <div className="uploadedDocs">
          {this.filteredDocuments().map(document => (
            <UploadConfirmation
              key={document.id}
              filename={document.original_filename}
              deleteHandler={() => this.deleteDocument(document.id)}
            />
          ))}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  documents: state.borrower.loanApplication.documents,
  documentType: ownProps.documentType,
  prompt: ownProps.prompt,
  onUpload: ownProps.onUpload,
  ownerEntityType: ownProps.ownerEntityType,
  ownerEntityID: ownProps.ownerEntityID,
});

const mapDispatchToProps = dispatch => ({
  updateApplication: (dispatchType, payload) => {
    dispatch({ payload, type: dispatchType });
  },
});

export { DocumentUploader };
const ConnectedDocumentUploader = connect(
  mapStateToProps,
  mapDispatchToProps,
)(DocumentUploader as any);
export default ConnectedDocumentUploader;
